<template>
  <!-- eslint-disable vue/no-v-html -->
  <span
    class="l-icon"
    :class="{ 'l-icon--left': left, 'l-icon--right': right }"
    :style="styleObj"
    aria-hidden="true"
    v-html="svg"
  ></span>
</template>

<script>
import lucide from '@/assets/lucideIconsSVG.json';

// Mapea los nombres mdi usados en la app a su equivalente en Lucide.
const MDI_TO_LUCIDE = {
  'mdi-account': 'user',
  'mdi-account-cog': 'user-cog',
  'mdi-alert-circle': 'circle-alert',
  'mdi-apple': 'apple',
  'mdi-arrow-left': 'arrow-left',
  'mdi-arrow-right': 'arrow-right',
  'mdi-briefcase': 'briefcase',
  'mdi-chart-line': 'trending-up',
  'mdi-check': 'check',
  'mdi-check-circle': 'circle-check',
  'mdi-chevron-down': 'chevron-down',
  'mdi-chevron-up': 'chevron-up',
  'mdi-chevron-left': 'chevron-left',
  'mdi-chevron-right': 'chevron-right',
  'mdi-close': 'x',
  'mdi-comment-quote-outline': 'message-square-quote',
  'mdi-creation': 'sparkles',
  'mdi-credit-card': 'credit-card',
  'mdi-cursor-default-click': 'mouse-pointer-click',
  'mdi-dots-vertical': 'ellipsis-vertical',
  'mdi-download': 'download',
  'mdi-email-plus-outline': 'mail-plus',
  'mdi-eye': 'eye',
  'mdi-eye-off': 'eye-off',
  'mdi-feather': 'feather',
  'mdi-filter-variant': 'filter',
  'mdi-flash': 'zap',
  'mdi-headset': 'headphones',
  'mdi-heart': 'heart',
  'mdi-information-outline': 'info',
  'mdi-lightning-bolt': 'zap',
  'mdi-lock': 'lock',
  'mdi-logout': 'log-out',
  'mdi-magnify-scan': 'scan-search',
  'mdi-menu': 'menu',
  'mdi-minus': 'minus',
  'mdi-newspaper-variant-outline': 'newspaper',
  'mdi-play-circle': 'circle-play',
  'mdi-plus': 'plus',
  'mdi-repeat': 'repeat',
  'mdi-robot': 'bot',
  'mdi-robot-happy': 'bot',
  'mdi-robot-happy-outline': 'bot',
  'mdi-rocket-launch': 'rocket',
  'mdi-school': 'graduation-cap',
  'mdi-send-check-outline': 'send',
  'mdi-shield-check': 'shield-check',
  'mdi-star': 'star',
  'mdi-star-outline': 'star',
  'mdi-tag': 'tag',
  'mdi-tag-outline': 'tag',
  'mdi-translate': 'languages',
  'mdi-weather-night': 'moon',
  'mdi-weather-sunny': 'sun',
};

// Colores semánticos de Vuetify usados en la app -> hex de marca Nukli.
const COLOR_MAP = {
  primary: '#2c7cff',
  secondary: '#6366f1',
  accent: '#06b6d4',
  success: '#22c55e',
  info: '#2c7cff',
  warning: '#f59e0b',
  error: '#ef4444',
  white: '#ffffff',
  black: '#0c193a',
  amber: '#f59e0b',
};

export default {
  name: 'LIcon',
  props: {
    name: { type: String, default: '' },
    color: { type: String, default: '' },
    size: { type: [Number, String], default: null },
    small: Boolean,
    xSmall: Boolean,
    large: Boolean,
    xLarge: Boolean,
    left: Boolean,
    right: Boolean,
  },
  computed: {
    rawName() {
      if (this.name) return this.name.trim();
      const slot = this.$slots.default;
      if (slot && slot[0] && slot[0].text) return slot[0].text.trim();
      return '';
    },
    lucideName() {
      const raw = this.rawName;
      if (!raw) return '';
      if (MDI_TO_LUCIDE[raw]) return MDI_TO_LUCIDE[raw];
      // Quita un prefijo mdi- por si no está mapeado, e intenta el nombre directo.
      return raw.replace(/^mdi-/, '');
    },
    svg() {
      return lucide[this.lucideName] || '';
    },
    pxSize() {
      if (this.size != null) {
        // size llega como string desde atributos (size="15"): sin unidad el
        // CSS es inválido y el SVG caía a sus 24px intrínsecos.
        const s = String(this.size).trim();
        return /^\d+(\.\d+)?$/.test(s) ? `${s}px` : s;
      }
      if (this.xSmall) return '14px';
      if (this.small) return '18px';
      if (this.large) return '30px';
      if (this.xLarge) return '38px';
      return '22px';
    },
    resolvedColor() {
      if (!this.color) return 'currentColor';
      const key = this.color.split(' ')[0]; // "amber darken-1" -> "amber"
      return COLOR_MAP[key] || this.color;
    },
    styleObj() {
      return {
        width: this.pxSize,
        height: this.pxSize,
        color: this.resolvedColor,
      };
    },
  },
};
</script>

<style scoped>
.l-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  flex: 0 0 auto;
}
.l-icon >>> svg {
  width: 100%;
  height: 100%;
  display: block;
}
.l-icon--left { margin-right: 8px; }
.l-icon--right { margin-left: 8px; }
</style>
